import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  CreateRawMaterialInput,
  ProductRef,
  RawMaterialDto,
  UnitDto,
  UpdateRawMaterialInput
} from '../../../viewmodels/useRawMaterials'

const NAME_MAX = 128
const DESC_MAX = 1024

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: RawMaterialDto | null
  readonly products: readonly ProductRef[]
  readonly units: readonly UnitDto[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateRawMaterialInput | UpdateRawMaterialInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  name: string
  description: string
  unit_id: string
  product_id: string
}

const EMPTY: FormState = {
  name: '',
  description: '',
  unit_id: '',
  product_id: ''
}

function clientValidate(state: FormState): { field: keyof FormState; message: string } | null {
  if (state.product_id === '') {
    return { field: 'product_id', message: 'product is required' }
  }
  const name = state.name.trim()
  if (name.length < 1 || name.length > NAME_MAX) {
    return { field: 'name', message: `name must be 1..${NAME_MAX} chars` }
  }
  if (state.unit_id === '') {
    return { field: 'unit_id', message: 'unit is required' }
  }
  if (state.description.length > DESC_MAX) {
    return { field: 'description', message: `description must be ≤${DESC_MAX} chars` }
  }
  return null
}

/**
 * RawMaterialFormModal — Add + Edit shared form. A raw material belongs
 * to exactly one product (required single-select) and carries a unit
 * label (e.g. "kg", "piece", "litre").
 */
export function RawMaterialFormModal({
  open,
  mode,
  initial,
  products,
  units,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? EMPTY
          : {
              name: initial.name,
              description: initial.description ?? '',
              unit_id: initial.unit_id ?? '',
              product_id: initial.product_id
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name)),
    [products]
  )

  // Unit options = current catalog + the row's existing unit if it's
  // been soft-deleted from the catalog (so the operator sees it as
  // "(removed) <name>" instead of an empty dropdown).
  const unitOptions = useMemo<UnitDto[]>(() => {
    const editingId = initial?.unit_id ?? null
    if (editingId !== null && !units.some((u) => u.id === editingId)) {
      const editingLabel = initial?.unit_label ?? '(removed unit)'
      return [{ id: editingId, name: `(removed) ${editingLabel}` }, ...units]
    }
    return [...units]
  }, [units, initial])

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      setFieldError({ field: clientErr.field, message: clientErr.message })
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const payload: CreateRawMaterialInput | UpdateRawMaterialInput = {
      name: state.name.trim(),
      description: state.description === '' ? null : state.description,
      unit_id: state.unit_id,
      product_id: state.product_id
    }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add raw material' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Product"
          error={fieldError?.field === 'product_id' ? fieldError.message : null}
        >
          <Select
            value={state.product_id}
            hasError={fieldError?.field === 'product_id'}
            onChange={(e) => setState((s) => ({ ...s, product_id: e.target.value }))}
            aria-label="Product"
          >
            <option value="">— Select a product —</option>
            {sortedProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Moso bamboo pole 3in"
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Unit"
          error={fieldError?.field === 'unit_id' ? fieldError.message : null}
        >
          <Select
            value={state.unit_id}
            hasError={fieldError?.field === 'unit_id'}
            onChange={(e) => setState((s) => ({ ...s, unit_id: e.target.value }))}
            aria-label="Unit"
          >
            <option value="">— Select a unit —</option>
            {unitOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </Select>
        </Field>

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={state.description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={DESC_MAX}
          />
        </Field>
      </div>
    </Modal>
  )
}
